// Settings → AI Assistant (Customer Experience).
//
// Configures the retailer-branded assistant shown on the CUSTOMER portal — it
// feels like the retailer's own staff member. Backed by:
//   GET /api/portal/settings/ai-assistant → { name, welcome, tone, enabled }
//   PUT /api/portal/settings/ai-assistant   save
//
// (Customers never see "AI"/TKOS — that wording lives only in this retailer-
// facing tab.)

import { iconMarkup } from "@tkos/shared-ui";

import type { PageContext } from "../../../shell/types";
import type { SettingsTab, TabCleanup } from "../types";

import "./ai-assistant.css";

interface AiSettings {
  ok?: boolean;
  name?: string;
  welcome?: string;
  tone?: string;
  enabled?: boolean;
}

const DEFAULT_WELCOME =
  "Hi! I'm here to help with anything about your new kitchen — your quote, payments, delivery, or any questions along the way.";

const aiAssistantTab: SettingsTab = {
  id: "ai-assistant",
  label: "AI Assistant",
  icon: iconMarkup("sparkle", { size: "sm" }),
  mount(container: HTMLElement, ctx: PageContext): TabCleanup {
    const ac = new AbortController();
    let saving = false;
    let saveTimer: number | null = null;

    const root = document.createElement("div");
    root.className = "set-ai-root";
    root.innerHTML = `
      <p class="set-ai-intro">
        Give your customers a branded assistant on their project portal — named, on-brand, and
        knowledgeable about their specific kitchen. It answers questions about their quote, payments,
        delivery and snags 24/7, and passes anything it can't handle to your team.
      </p>
      <div class="set-ai-card">
        <label class="set-ai-field">
          <span class="set-ai-label">Assistant name <em>required</em></span>
          <input type="text" class="set-ai-input" data-role="name" maxlength="100"
                 placeholder="e.g. Emma, Alex, Sam" />
          <span class="set-ai-hint">Customers see this name at the top of the chat and in its sign-off.</span>
        </label>
        <label class="set-ai-field">
          <span class="set-ai-label">Tone <em>optional</em></span>
          <input type="text" class="set-ai-input" data-role="tone" maxlength="500"
                 placeholder="e.g. Friendly and informal / Professional and precise" />
          <span class="set-ai-hint">How the assistant should speak. Leave blank for warm and helpful.</span>
        </label>
        <label class="set-ai-field">
          <span class="set-ai-label">Welcome message <em>optional</em></span>
          <textarea class="set-ai-input set-ai-textarea" data-role="welcome" rows="3" maxlength="2000"
                    placeholder="${DEFAULT_WELCOME.replace(/"/g, "&quot;")}"></textarea>
          <span class="set-ai-hint">The first thing the assistant says. Leave blank to use the default.</span>
        </label>
        <label class="set-ai-toggle">
          <input type="checkbox" data-role="enabled" />
          <span>Enabled — show the assistant on the customer portal</span>
        </label>
        <div class="set-ai-footer">
          <span class="set-ai-status" data-role="status"></span>
          <button type="button" class="set-btn set-btn--primary" data-role="save">Save</button>
        </div>
      </div>`;
    container.appendChild(root);

    const nameEl    = root.querySelector<HTMLInputElement>("[data-role='name']")!;
    const toneEl    = root.querySelector<HTMLInputElement>("[data-role='tone']")!;
    const welcomeEl = root.querySelector<HTMLTextAreaElement>("[data-role='welcome']")!;
    const enabledEl = root.querySelector<HTMLInputElement>("[data-role='enabled']")!;
    const statusEl  = root.querySelector<HTMLElement>("[data-role='status']")!;
    const saveBtn   = root.querySelector<HTMLButtonElement>("[data-role='save']")!;

    const setStatus = (msg: string, kind: "" | "saved" | "error" = ""): void => {
      statusEl.textContent = msg;
      statusEl.className = `set-ai-status${kind ? ` set-ai-status--${kind}` : ""}`;
    };

    const load = async (): Promise<void> => {
      setStatus("Loading…");
      try {
        const data = await ctx.api<AiSettings>("/portal/settings/ai-assistant", { signal: ac.signal });
        if (ac.signal.aborted) return;
        nameEl.value = data.name ?? "";
        toneEl.value = data.tone ?? "";
        welcomeEl.value = data.welcome ?? "";
        enabledEl.checked = data.enabled !== false;
        setStatus("");
      } catch (err) {
        if ((err as { name?: string }).name === "AbortError") return;
        setStatus("Couldn't load settings.", "error");
      }
    };

    const save = async (): Promise<void> => {
      if (saving) return;
      const name = nameEl.value.trim();
      if (!name) { setStatus("Give your assistant a name first.", "error"); nameEl.focus(); return; }
      saving = true;
      setStatus("Saving…");
      try {
        await ctx.api("/portal/settings/ai-assistant", {
          method: "PUT",
          body: JSON.stringify({
            name,
            tone: toneEl.value.trim(),
            welcome: welcomeEl.value.trim(),
            enabled: enabledEl.checked,
          }),
          signal: ac.signal,
        });
        setStatus("Saved.", "saved");
      } catch (err) {
        if ((err as { name?: string }).name === "AbortError") return;
        setStatus(err instanceof Error ? err.message : "Save failed.", "error");
      } finally {
        saving = false;
      }
    };

    saveBtn.addEventListener("click", () => void save());
    // Auto-save the enabled toggle (debounced) for a snappy on/off.
    enabledEl.addEventListener("change", () => {
      if (saveTimer !== null) window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(() => { saveTimer = null; if (nameEl.value.trim()) void save(); }, 300);
    });

    void load();

    return () => {
      ac.abort();
      if (saveTimer !== null) window.clearTimeout(saveTimer);
      root.remove();
    };
  },
};

export default aiAssistantTab;
